import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    CreditCard, Settings, Eye, EyeOff, Check, X, Loader2, 
    RefreshCw, Shield, AlertTriangle, ExternalLink, TestTube,
    Wallet, Building2
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Payment provider icons/logos
const ProviderIcon = ({ provider }) => {
    const icons = {
        moyasar: (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
            </div>
        ),
        paylink: (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">PL</span>
            </div>
        ),
        stripe: (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
            </div>
        ),
        paypal: (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
                <span className="text-white font-bold text-sm">PP</span>
            </div>
        )
    };
    return icons[provider] || <Wallet className="w-10 h-10 text-slate-400" />;
};

const AdminPaymentsPage = () => {
    const { api, isSuperAdmin } = useAdmin();
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showSecrets, setShowSecrets] = useState({});
    
    // Form state
    const [formData, setFormData] = useState({
        api_key: '',
        secret_key: '',
        publishable_key: '',
        merchant_id: '',
        webhook_secret: '',
        is_sandbox: true
    });

    const fetchPaymentMethods = useCallback(async () => {
        try {
            const response = await api.get('/admin/payment-methods');
            setPaymentMethods(response.data);
        } catch (error) {
            toast.error('Failed to fetch payment methods');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    const openConfigDialog = async (method) => {
        setSelectedMethod(method);
        // Fetch detailed config
        try {
            const response = await api.get(`/admin/payment-methods/${method.provider}`);
            setFormData({
                api_key: '',  // Don't prefill secret keys
                secret_key: '',
                publishable_key: response.data.publishable_key || '',
                merchant_id: response.data.merchant_id || '',
                webhook_secret: '',
                is_sandbox: response.data.is_sandbox ?? true
            });
        } catch (error) {
            console.error('Failed to fetch method details:', error);
        }
        setConfigDialogOpen(true);
    };

    const handleSaveConfig = async () => {
        if (!selectedMethod) return;
        setSaving(true);
        
        try {
            // Only send non-empty values
            const updateData = { is_sandbox: formData.is_sandbox };
            if (formData.api_key) updateData.api_key = formData.api_key;
            if (formData.secret_key) updateData.secret_key = formData.secret_key;
            if (formData.publishable_key) updateData.publishable_key = formData.publishable_key;
            if (formData.merchant_id) updateData.merchant_id = formData.merchant_id;
            if (formData.webhook_secret) updateData.webhook_secret = formData.webhook_secret;
            
            await api.put(`/admin/payment-methods/${selectedMethod.provider}`, updateData);
            toast.success(`${selectedMethod.display_name} configuration saved`);
            setConfigDialogOpen(false);
            fetchPaymentMethods();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (provider, currentStatus) => {
        try {
            await api.put(`/admin/payment-methods/${provider}/toggle?enabled=${!currentStatus}`);
            toast.success(`Payment method ${!currentStatus ? 'enabled' : 'disabled'}`);
            fetchPaymentMethods();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to toggle payment method');
        }
    };

    const handleTestConnection = async (provider) => {
        setTesting(true);
        try {
            const response = await api.post(`/admin/payment-methods/${provider}/test`);
            if (response.data.success) {
                toast.success(response.data.message);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Connection test failed');
        } finally {
            setTesting(false);
        }
    };

    const toggleShowSecret = (field) => {
        setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-payments-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Payment Methods</h1>
                    <p className="text-slate-400 mt-1">Configure and manage payment gateways</p>
                </div>
                <Button 
                    variant="outline" 
                    onClick={fetchPaymentMethods}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {!isSuperAdmin && (
                <Card className="border-amber-500/50 bg-amber-500/10">
                    <CardContent className="p-4 flex items-center gap-3">
                        <Shield className="w-5 h-5 text-amber-400" />
                        <p className="text-amber-200 text-sm">
                            Only super admins can modify payment configurations.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Payment Methods Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {paymentMethods.map((method) => (
                    <Card 
                        key={method.provider} 
                        className={`border-slate-700 bg-slate-800/50 ${method.is_enabled ? 'ring-2 ring-teal-500/50' : ''}`}
                        data-testid={`payment-method-${method.provider}`}
                    >
                        <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <ProviderIcon provider={method.provider} />
                                    <div>
                                        <CardTitle className="text-lg text-white flex items-center gap-2">
                                            {method.display_name}
                                            {method.is_enabled && (
                                                <Badge className="bg-teal-500/20 text-teal-400 border-0">Active</Badge>
                                            )}
                                        </CardTitle>
                                        <CardDescription className="text-slate-400 text-sm mt-1">
                                            {method.description}
                                        </CardDescription>
                                    </div>
                                </div>
                                {isSuperAdmin && (
                                    <Switch
                                        checked={method.is_enabled}
                                        onCheckedChange={() => handleToggle(method.provider, method.is_enabled)}
                                        disabled={!method.is_configured}
                                        data-testid={`toggle-${method.provider}`}
                                    />
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Status indicators */}
                            <div className="flex flex-wrap gap-2">
                                <Badge variant="outline" className={`${method.is_configured ? 'border-green-500/50 text-green-400' : 'border-slate-600 text-slate-400'}`}>
                                    {method.is_configured ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                                    {method.is_configured ? 'Configured' : 'Not Configured'}
                                </Badge>
                                {method.is_configured && (
                                    <Badge variant="outline" className={`${method.is_sandbox ? 'border-amber-500/50 text-amber-400' : 'border-green-500/50 text-green-400'}`}>
                                        {method.is_sandbox ? 'Sandbox' : 'Live'}
                                    </Badge>
                                )}
                            </div>

                            {/* Supported currencies */}
                            <div>
                                <p className="text-xs text-slate-500 mb-1">Supported Currencies</p>
                                <div className="flex gap-1">
                                    {method.supported_currencies.map(currency => (
                                        <Badge key={currency} variant="secondary" className="bg-slate-700 text-slate-300 text-xs">
                                            {currency}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Features */}
                            <div>
                                <p className="text-xs text-slate-500 mb-1">Features</p>
                                <div className="flex flex-wrap gap-1">
                                    {method.features?.map(feature => (
                                        <Badge key={feature} variant="outline" className="border-slate-600 text-slate-400 text-xs">
                                            {feature.replace('_', ' ')}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Credential status */}
                            {method.is_configured && (
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="flex items-center gap-1">
                                        {method.has_api_key ? <Check className="w-3 h-3 text-green-400" /> : <X className="w-3 h-3 text-slate-500" />}
                                        <span className="text-slate-400">API Key</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {method.has_secret_key ? <Check className="w-3 h-3 text-green-400" /> : <X className="w-3 h-3 text-slate-500" />}
                                        <span className="text-slate-400">Secret Key</span>
                                    </div>
                                    {method.required_fields?.includes('publishable_key') && (
                                        <div className="flex items-center gap-1">
                                            {method.has_publishable_key ? <Check className="w-3 h-3 text-green-400" /> : <X className="w-3 h-3 text-slate-500" />}
                                            <span className="text-slate-400">Publishable Key</span>
                                        </div>
                                    )}
                                    {method.required_fields?.includes('merchant_id') && (
                                        <div className="flex items-center gap-1">
                                            {method.has_merchant_id ? <Check className="w-3 h-3 text-green-400" /> : <X className="w-3 h-3 text-slate-500" />}
                                            <span className="text-slate-400">Merchant ID</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Actions */}
                            {isSuperAdmin && (
                                <div className="flex gap-2 pt-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openConfigDialog(method)}
                                        className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                                        data-testid={`configure-${method.provider}`}
                                    >
                                        <Settings className="w-4 h-4 mr-1" />
                                        Configure
                                    </Button>
                                    {method.is_configured && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleTestConnection(method.provider)}
                                            disabled={testing}
                                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                            data-testid={`test-${method.provider}`}
                                        >
                                            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                                        </Button>
                                    )}
                                </div>
                            )}

                            {method.last_updated && (
                                <p className="text-xs text-slate-500">
                                    Last updated: {new Date(method.last_updated).toLocaleString()}
                                </p>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Configuration Dialog */}
            <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {selectedMethod && <ProviderIcon provider={selectedMethod.provider} />}
                            Configure {selectedMethod?.display_name}
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Enter your API credentials. Leave fields empty to keep existing values.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Sandbox/Live Mode */}
                        <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                            <div>
                                <Label className="text-slate-300">Environment Mode</Label>
                                <p className="text-xs text-slate-500 mt-1">
                                    {formData.is_sandbox ? 'Using test/sandbox credentials' : 'Using live/production credentials'}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`text-sm ${formData.is_sandbox ? 'text-amber-400' : 'text-slate-500'}`}>Sandbox</span>
                                <Switch
                                    checked={!formData.is_sandbox}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_sandbox: !checked }))}
                                />
                                <span className={`text-sm ${!formData.is_sandbox ? 'text-green-400' : 'text-slate-500'}`}>Live</span>
                            </div>
                        </div>

                        {!formData.is_sandbox && (
                            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                <p className="text-red-400 text-sm">Live mode will process real payments!</p>
                            </div>
                        )}

                        {/* API Key */}
                        <div className="space-y-2">
                            <Label className="text-slate-300">API Key *</Label>
                            <div className="relative">
                                <Input
                                    type={showSecrets.api_key ? 'text' : 'password'}
                                    value={formData.api_key}
                                    onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                                    placeholder={selectedMethod?.has_api_key ? '••••••••••••' : 'Enter API key'}
                                    className="pr-10 bg-slate-700/50 border-slate-600 text-white"
                                />
                                <button
                                    type="button"
                                    onClick={() => toggleShowSecret('api_key')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                >
                                    {showSecrets.api_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {selectedMethod?.api_key_masked && (
                                <p className="text-xs text-slate-500">Current: {selectedMethod.api_key_masked}</p>
                            )}
                        </div>

                        {/* Secret Key */}
                        <div className="space-y-2">
                            <Label className="text-slate-300">Secret Key *</Label>
                            <div className="relative">
                                <Input
                                    type={showSecrets.secret_key ? 'text' : 'password'}
                                    value={formData.secret_key}
                                    onChange={(e) => setFormData(prev => ({ ...prev, secret_key: e.target.value }))}
                                    placeholder={selectedMethod?.has_secret_key ? '••••••••••••' : 'Enter secret key'}
                                    className="pr-10 bg-slate-700/50 border-slate-600 text-white"
                                />
                                <button
                                    type="button"
                                    onClick={() => toggleShowSecret('secret_key')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                >
                                    {showSecrets.secret_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Publishable Key (for Stripe/Moyasar) */}
                        {selectedMethod?.required_fields?.includes('publishable_key') && (
                            <div className="space-y-2">
                                <Label className="text-slate-300">Publishable Key</Label>
                                <Input
                                    type="text"
                                    value={formData.publishable_key}
                                    onChange={(e) => setFormData(prev => ({ ...prev, publishable_key: e.target.value }))}
                                    placeholder="Enter publishable key"
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                />
                                <p className="text-xs text-slate-500">This key is safe to expose on the frontend</p>
                            </div>
                        )}

                        {/* Merchant ID (for Paylink) */}
                        {selectedMethod?.required_fields?.includes('merchant_id') && (
                            <div className="space-y-2">
                                <Label className="text-slate-300">Merchant ID</Label>
                                <Input
                                    type="text"
                                    value={formData.merchant_id}
                                    onChange={(e) => setFormData(prev => ({ ...prev, merchant_id: e.target.value }))}
                                    placeholder="Enter merchant ID"
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                />
                            </div>
                        )}

                        {/* Webhook Secret (for Stripe) */}
                        {selectedMethod?.required_fields?.includes('webhook_secret') && (
                            <div className="space-y-2">
                                <Label className="text-slate-300">Webhook Secret</Label>
                                <div className="relative">
                                    <Input
                                        type={showSecrets.webhook_secret ? 'text' : 'password'}
                                        value={formData.webhook_secret}
                                        onChange={(e) => setFormData(prev => ({ ...prev, webhook_secret: e.target.value }))}
                                        placeholder={selectedMethod?.has_webhook_secret ? '••••••••••••' : 'Enter webhook secret'}
                                        className="pr-10 bg-slate-700/50 border-slate-600 text-white"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => toggleShowSecret('webhook_secret')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                    >
                                        {showSecrets.webhook_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setConfigDialogOpen(false)}
                            className="border-slate-600 text-slate-300"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSaveConfig}
                            disabled={saving}
                            className="bg-teal-500 hover:bg-teal-600"
                        >
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Save Configuration
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminPaymentsPage;
